import { memo } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import {
    euclideanDistanceMm,
    PointMm,
} from '~/domain/geometry/patientGeometry';
import {
    LineProfile,
    LineProfileDraft,
    lineProfileInFrame,
    ProfileEndpoint,
    withMovedEndpoint,
} from '~/domain/intensity/lineProfile';
import { paneImageOf } from '~/engine/view/paneImage';
import {
    isPointInsideDisplayedImage,
    viewShowsPlane,
} from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import {
    AnnotationPointDrag,
    ontoShownPane,
    useAnnotationPointDrag,
    usePlacedAnnotation,
} from '~/hooks/viewer/annotationOverlay';
import { updateAnnotation } from '~/state/atoms/annotationListAtoms';
import {
    lineProfileListState,
    PROFILE_HOVER_TOLERANCE_MM,
    profileHoverDistanceState,
} from '~/state/atoms/intensityToolAtoms';
import { INTENSITY_FAMILY_COLOR } from '~/utils/annotationKinds';
import { formatDistanceCm } from '~/utils/measurementFormat';
import {
    CanvasProjection,
    canvasProjection,
    DASHED,
    distanceLabelPos,
    HIT_BAND,
    PlacedAnnotationProps,
    placedShapeStyle,
    PlacedShape,
    SHAPE_STROKE_PX,
} from '../AnnotationOverlaySvg';

const HANDLE_PX = 4;

interface EndpointDrag {
    readonly id: string;
    readonly endpoint: ProfileEndpoint;
    /**
     * The endpoint that stays put. The dragged point is kept on the plane
     * through it, so a profile cannot come out of an edit spanning two slices.
     */
    readonly anchorMm: PointMm;
}

/** Both endpoints must lie on the shown slice for a profile to be on it. */
function profilePoints(profile: LineProfile): readonly PointMm[] {
    return [profile.startMm, profile.endMm];
}

/**
 * Endpoint drag. The moved point is pinned to the plane through the profile's
 * other endpoint, measured along the view's current normal: that keeps the
 * profile single-slice (SR-WOR-H-17-4) while still ignoring the slice position
 * stored at placement, which on a rotated view was measured along a different
 * normal and would drop the endpoint onto an offset plane. Moves that leave
 * the image are refused, and so is every move once the view no longer shows
 * the profile's image. The moved point is stored in the frame of the image
 * the pane shows.
 */
export function useEndpointDrag(
    view: PlaneView | null
): AnnotationPointDrag<EndpointDrag> {
    const movePoint = useRecoilCallback(
        (callback) =>
            (drag: EndpointDrag, pointMm: PointMm, dragView: PlaneView) => {
                const pane = paneImageOf(dragView);
                if (
                    pane.kind === 'none' ||
                    !isPointInsideDisplayedImage(pointMm, dragView)
                ) {
                    return;
                }
                updateAnnotation(
                    callback,
                    lineProfileListState,
                    drag.id,
                    (profile) =>
                        viewShowsPlane(dragView, profile.plane)
                            ? withMovedEndpoint(
                                  profile,
                                  drag.endpoint,
                                  pane.toImageFrame(pointMm)
                              )
                            : profile
                );
            },
        []
    );

    return useAnnotationPointDrag(view, movePoint);
}

/**
 * Draws one placed line profile on a tile, with its length as its label and,
 * while it is selected, a ring above every stroke where its chart is pointed.
 */
function LineProfileOverlay(props: PlacedAnnotationProps) {
    const { id, view, frame, handleLayer } = props;
    const { beginDrag, dragging } = useEndpointDrag(view);
    const hoverMm = useRecoilValue(profileHoverDistanceState(id));
    const placed = usePlacedAnnotation(
        props,
        lineProfileListState,
        lineProfileInFrame,
        profilePoints,
        dragging !== null
    );
    if (!placed) {
        return null;
    }
    const { startMm, endMm } = placed.onPane;
    const { toCanvas } = canvasProjection(view, frame);
    const start = toCanvas(startMm);
    const end = toCanvas(endMm);
    const line = { x1: start.x, y1: start.y, x2: end.x, y2: end.y };
    const lengthMm = euclideanDistanceMm(startMm, endMm);
    const ringMm = (placed.selected ? hoverMm : null) ?? NaN;
    // The projection is affine, so it keeps the fraction along the line.
    const along = Math.min(Math.max(ringMm / lengthMm, 0), 1);
    const at = {
        cx: start.x + (end.x - start.x) * along,
        cy: start.y + (end.y - start.y) * along,
        r: 5,
        fill: 'none',
    };
    const ring = lengthMm > 0 &&
        ringMm >= -PROFILE_HOVER_TOLERANCE_MM &&
        ringMm <= lengthMm + PROFILE_HOVER_TOLERANCE_MM && (
            <g data-testid="profile-hover-ring">
                <circle {...at} stroke="rgba(0, 0, 0, 0.75)" strokeWidth={4} />
                <circle {...at} stroke="#fff" strokeWidth={2} />
            </g>
        );
    return (
        <PlacedShape
            {...props}
            placed={placed}
            mode="profile"
            label={formatDistanceCm(lengthMm)}
            labelAt={distanceLabelPos(start, end)}
            bands={<line {...line} {...HIT_BAND} />}
            handles={[start, end]}
            beginDrag={(event, handle) =>
                beginDrag(event, {
                    id,
                    endpoint: handle ? 'end' : 'start',
                    anchorMm: handle ? startMm : endMm,
                })
            }
        >
            <line
                {...line}
                stroke={INTENSITY_FAMILY_COLOR}
                {...placedShapeStyle(placed)}
            />
            {ring && handleLayer && createPortal(ring, handleLayer)}
        </PlacedShape>
    );
}

export default memo(LineProfileOverlay);

/** Render the in-progress placement: start dot + rubber-band to the cursor. */
export function renderDraft(
    view: PlaneView,
    { toCanvas }: CanvasProjection,
    stored: LineProfileDraft | null
) {
    const draft = stored && ontoShownPane(view, stored, lineProfileInFrame);
    if (!draft) {
        return null;
    }
    const start = toCanvas(draft.startMm);
    if (!draft.endMm) {
        return (
            <circle
                key="draft"
                cx={start.x}
                cy={start.y}
                r={HANDLE_PX}
                fill={INTENSITY_FAMILY_COLOR}
            />
        );
    }
    const end = toCanvas(draft.endMm);
    return (
        <g key="draft">
            <line
                x1={start.x}
                y1={start.y}
                x2={end.x}
                y2={end.y}
                stroke={INTENSITY_FAMILY_COLOR}
                strokeWidth={SHAPE_STROKE_PX}
                strokeDasharray={DASHED}
            />
            <circle
                cx={start.x}
                cy={start.y}
                r={HANDLE_PX}
                fill={INTENSITY_FAMILY_COLOR}
            />
        </g>
    );
}
